import { initializeYup, EducationType, isArrayEmpty } from "@duosoftbg/nacid-components";
import * as yup from "yup";
import i18n from "i18next";

const createEducationEntryADQValidationSchema = () => {
  initializeYup(yup);
  const educationEntryADQValidationSchema = yup.object({
    oldEducationInstitutionName: yup
      .string()
      .test("oldEducationInstitutionName", i18n.t("validation.field.required"), function (value) {
        const { newEducationInstitutionName } = this.parent;
        if (!newEducationInstitutionName) return value !== null && value !== "";
        return true;
      }),
    newEducationInstitutionName: yup
      .string()
      .test("newEducationInstitutionName", i18n.t("validation.field.required"), function (value) {
        const { oldEducationInstitutionName } = this.parent;
        if (!oldEducationInstitutionName) return value !== null && value !== "";
        return true;
      }),
    professionalQualification: yup.string().required(),
    specialities: yup.array(),
    specialitySingle: yup
      .object({
        id: yup.string(),
        name: yup.string(),
      })
      .nullable()
      .test("required-when-no-specialities", i18n.t("validation.field.required"), function (value) {
        const specialities = this.parent?.specialities;
        const isSpecialitiesEmpty = isArrayEmpty(specialities);
        const isSpecialitySingleEmpty = !value || !value.name;
        if (isSpecialitiesEmpty && isSpecialitySingleEmpty) {
          return this.createError({
            path: this.path + ".name",
            message: i18n.t("validation.field.required"),
          });
        }
        return true;
      }),
    documentKind: yup.object({
      id: yup.string().required(),
    }),
  });
  return educationEntryADQValidationSchema;
};

const createEducationEntryHigherProfValidationSchema = () => {
  initializeYup(yup);
  const educationEntryHigherProfValidationSchema = yup.object({}).concat(createEducationEntryADQValidationSchema());
  return educationEntryHigherProfValidationSchema;
};

const createEducationValidationSchema = () => {
  initializeYup(yup);

  const educationValidationSchema = yup.object({
    kind: yup
      .string()
      .optional()
      .nullable()
      .test("required", i18n.t("validation.field.required"), function (value) {
        return Object.values(EducationType).includes(EducationType[value]);
      }),
    educationEntryHigher: yup.object().when("kind", {
      is: (kind) => kind === EducationType.HIGHER_EDUCATION,
      then: createEducationEntryHigherProfValidationSchema(),
    }),
    educationEntrySecondary: yup.object().when("kind", {
      is: (kind) =>
        kind === EducationType.SECONDARY_PROFESSIONAL_EDUCATION || kind === EducationType.PROFESSIONAL_EDUCATION,
      then: createEducationEntryHigherProfValidationSchema(),
    }),
    educationEntryADQ: yup.object().when("kind", {
      is: (kind) => kind === EducationType.AFTER_DIPLOMA_QUALIFICATION,
      then: createEducationEntryADQValidationSchema(),
    }),
  });
  return educationValidationSchema;
};

const createWorkPeriodsValidationSchema = () => {
  initializeYup(yup);
  const wpValidationSchema = yup.array().of(
    yup.object().shape({
      fromDate: yup.string().required(),
      toDate: yup.string().required(),
      workDayHours: yup.object({
        id: yup.string().required(),
      }),
    }),
  );
  return wpValidationSchema;
};

export const createExperienceDocumentsOnlyPeriodsValidationSchema = () => {
  return yup.array().of(
    yup.object().shape({
      workPeriods: createWorkPeriodsValidationSchema(),
    }),
  );
};

const createExperienceValidationSchema = () => {
  initializeYup(yup);

  const experienceValidationSchema = yup.object({
    profession: yup.string().required(),
    experienceDocuments: yup.array().of(
      yup.object().shape({
        type: yup.object({
          id: yup.string().required(),
        }),
        institutionName: yup.string().required(),
        workPeriods: createWorkPeriodsValidationSchema(),
      }),
    ),
  });
  return experienceValidationSchema;
};

export const createRegprofEducationDetailsValidationSchema = () => {
  initializeYup(yup);

  const educationDetailsValidationSchema = yup.object({
    serviceType: yup.object({
      id: yup.string().required(),
    }),
    country: yup.object({
      id: yup.string().required(),
    }),
    education: yup.object().when("educationSelected", {
      is: true,
      then: createEducationValidationSchema(),
    }),
    experience: yup.object().when("experienceSelected", {
      is: true,
      then: createExperienceValidationSchema(),
    }),
    nonRevokedRightToPractice: yup
      .boolean()
      .test("required", i18n.t("validation.field.required"), (value) => value === true),
    professionalQualificationRequested: yup.string().required(),
  });

  return educationDetailsValidationSchema;
};
